'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import { isSubscriber } from '@alltrails/context/utils/isSubscriber';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import memberDealsDesktopSrc from './AT-DotCom-ProDealsHomepage-D.webp';
import memberDealsMobileSrc from './AT-DotCom-ProDealsHomepage-M.webp';
import gearMobileSrc from './gear-mobile.avif';
import gearDesktopSrc from './gear.avif';
import styles from './GearShop.module.scss';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

// Design: https://www.figma.com/design/6VHX3c5WyrLT83mIUBJiLU/2026-Homepage-update--Copy-?node-id=686-11728

type GearShopProps = {
  tout?: string;
};

const GearShop = ({ tout }: GearShopProps) => {
  const isMobile = useIsMobileSizedScreen();
  const isMemberDeals = tout === 'memberDeals';
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);

  const gearShopLink = process.env.NEXT_PUBLIC_GEAR_SHOP_LINK;
  const memberDealsLink = process.env.NEXT_PUBLIC_MEMBER_DEALS_LINK;

  const { images, altText, titleText, subtitleText, buttonText, buttonLink } = isMemberDeals
    ? {
        images: { mobile: memberDealsMobileSrc, desktop: memberDealsDesktopSrc },
        altText:
          'An assortment of hiking gear, outdoor accessories, and travel images from Outdoorsy, Black Diamond, Topo Designs, Rumpl, and Going. All are arranged on a black background.',
        titleText: 'New! Exclusive deals for premium members',
        subtitleText: 'Level up your adventures with up to 50% off top outdoor brands.',
        buttonText: 'View deals',
        buttonLink: isSubscriber(user) ? memberDealsLink : '/plans/member-deals'
      }
    : {
        images: { mobile: gearMobileSrc, desktop: gearDesktopSrc },
        altText: 'An assortment of AllTrails hiking gear and outdoor accessories arranged on a green background.',
        titleText: 'Gear up and get out there',
        subtitleText: 'See what’s new this season',
        buttonText: 'Shop AllTrails Gear',
        buttonLink: gearShopLink
      };

  const buttonProps =
    isMemberDeals && !user
      ? {
          onClick: () => {
            setIsSignupModalOpen(true);
          }
        }
      : { linkInfo: { href: buttonLink } };

  return (
    <div className={styles.container}>
      <div className={classNames(styles.imageWrapper, isMemberDeals && styles.memberDeals)}>
        <Image src={isMobile ? images.mobile : images.desktop} alt={altText} sizes="100vw" className={styles.image} />
        <div className={styles.contentWrapper}>
          <div className={styles.content}>
            <Heading size={isMobile ? '400' : '500'} as="h2" className={styles.title}>
              {titleText}
            </Heading>
            <Typography variant={isMobile ? 'paragraph100' : 'paragraph200'} className={styles.description} component="p">
              {subtitleText}
            </Typography>
            <Button text={buttonText} {...buttonProps} variant="accent" size="md" />
          </div>
        </div>
      </div>
      {isSignupModalOpen && (
        <SignUpModal
          closeModal={() => setIsSignupModalOpen(false)}
          isOpen={isSignupModalOpen}
          onSuccess={signedUpUser => {
            setIsSignupModalOpen(false);
            window.location.assign(isSubscriber(signedUpUser) ? memberDealsLink : wrapUrlSafe('/plans/member-deals', languageRegionCode));
          }}
          trigger="Member Deals"
          upsellTrigger="Member Deals"
        />
      )}
    </div>
  );
};

export default GearShop;
